import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdAdd, MdEdit, MdDelete, MdSearch, MdVisibility, MdLocalShipping } from 'react-icons/md';
import '../master/MasterData.css';

interface Customer {
  customerId: number;
  customerCode: string;
  customerName: string;
  customerType: string;
  tin: string;
  email: string;
  phone: string;
  city: string;
  isActive: string;
}

interface OrderItem {
  itemId: number;
  itemCode: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  amount: number;
}

interface SalesOrder {
  orderId: number;
  orderNo: string;
  orderDate: string;
  customerCode: string;
  customerName: string;
  deliveryDate: string;
  totalAmount: number;
  status: 'PENDING' | 'CONFIRMED' | 'IN_PRODUCTION' | 'READY' | 'DELIVERED' | 'CANCELLED';
  items: OrderItem[];
  shippingAddress: string;
  remarks: string;
}

const SalesOrders: React.FC = () => {
  const [orders, setOrders] = useState<SalesOrder[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState<SalesOrder | null>(null);
  const [viewMode, setViewMode] = useState(false);

  useEffect(() => {
    // TODO: Fetch customers from API
    // Mock data for now
    setCustomers([
      {
        customerId: 1,
        customerCode: 'CUST001',
        customerName: 'SM Furniture Store',
        customerType: 'CORPORATE',
        tin: '123-456-789-000',
        email: 'sm@furniture.ph',
        phone: '+63 2 1234 5678',
        city: 'Manila',
        isActive: 'Y'
      },
      {
        customerId: 2,
        customerCode: 'CUST002',
        customerName: 'Home Depot Philippines',
        customerType: 'CORPORATE',
        tin: '987-654-321-000',
        email: 'homedepot@example.ph',
        phone: '+63 2 9876 5432',
        city: 'Quezon City',
        isActive: 'Y'
      }
    ]);

    // TODO: Fetch orders from API
    // Mock data
    setOrders([
      {
        orderId: 1,
        orderNo: 'SO-2025-0001',
        orderDate: '2025-01-16',
        customerCode: 'CUST001',
        customerName: 'SM Furniture Store',
        deliveryDate: '2025-02-15',
        totalAmount: 450000,
        status: 'CONFIRMED',
        items: [],
        shippingAddress: '123 Main St, Manila',
        remarks: 'Rush order'
      },
      {
        orderId: 2,
        orderNo: 'SO-2025-0002',
        orderDate: '2025-01-21',
        customerCode: 'CUST002',
        customerName: 'Home Depot Philippines',
        deliveryDate: '2025-02-25',
        totalAmount: 850000,
        status: 'IN_PRODUCTION',
        items: [],
        shippingAddress: '456 QC Ave, Quezon City',
        remarks: ''
      }
    ]);
  }, []);

  const filteredOrders = orders.filter(order =>
    order.orderNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customerName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingOrder(null);
    setViewMode(false);
    setShowModal(true);
  };

  const handleEdit = (order: SalesOrder) => {
    setEditingOrder(order);
    setViewMode(false);
    setShowModal(true);
  };

  const handleView = (order: SalesOrder) => {
    setEditingOrder(order);
    setViewMode(true);
    setShowModal(true);
  };

  const handleDelete = (orderId: number) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      setOrders(orders.filter(o => o.orderId !== orderId));
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'CONFIRMED': return 'badge-success';
      case 'IN_PRODUCTION': return 'badge-info';
      case 'READY': return 'badge-warning';
      case 'DELIVERED': return 'badge-success';
      case 'CANCELLED': return 'badge-danger';
      default: return 'badge-secondary';
    }
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Sales Orders</h2>
            <p className="subtitle">Manage customer orders and fulfillment</p>
          </div>
          <button className="btn btn-primary" onClick={handleAdd}>
            <MdAdd /> Create New Order
          </button>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Order No</th>
                  <th>Order Date</th>
                  <th>Customer</th>
                  <th>Delivery Date</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map(order => (
                  <tr key={order.orderId}>
                    <td className="code">{order.orderNo}</td>
                    <td>{new Date(order.orderDate).toLocaleDateString('en-US')}</td>
                    <td>{order.customerName}</td>
                    <td>{new Date(order.deliveryDate).toLocaleDateString('en-US')}</td>
                    <td className="amount">₱{order.totalAmount.toLocaleString()}</td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(order.status)}`}>
                        {order.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-view"
                          onClick={() => handleView(order)}
                          title="View"
                        >
                          <MdVisibility />
                        </button>
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEdit(order)}
                          title="Edit"
                        >
                          <MdEdit />
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => handleDelete(order.orderId)}
                          title="Delete"
                        >
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredOrders.length === 0 && (
              <div className="no-data">
                <p>No orders found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <OrderModal
          order={editingOrder}
          viewMode={viewMode}
          customers={customers}
          onClose={() => setShowModal(false)}
          onSave={(order) => {
            if (editingOrder) {
              setOrders(orders.map(o =>
                o.orderId === order.orderId ? order : o
              ));
            } else {
              setOrders([...orders, { ...order, orderId: Date.now() }]);
            }
            setShowModal(false);
          }}
        />
      )}
    </MainLayout>
  );
};

interface OrderModalProps {
  order: SalesOrder | null;
  viewMode: boolean;
  customers: Customer[];
  onClose: () => void;
  onSave: (order: SalesOrder) => void;
}

const OrderModal: React.FC<OrderModalProps> = ({ order, viewMode, customers, onClose, onSave }) => {
  const [formData, setFormData] = useState<SalesOrder>(
    order || {
      orderId: 0,
      orderNo: '',
      orderDate: new Date().toISOString().split('T')[0],
      customerCode: '',
      customerName: '',
      deliveryDate: '',
      totalAmount: 0,
      status: 'PENDING',
      items: [],
      shippingAddress: '',
      remarks: ''
    }
  );

  const handleCustomerChange = (customerCode: string) => {
    const selectedCustomer = customers.find(c => c.customerCode === customerCode);
    if (selectedCustomer) {
      setFormData({
        ...formData,
        customerCode: selectedCustomer.customerCode,
        customerName: selectedCustomer.customerName
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!viewMode) {
      onSave(formData);
    }
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        {
          itemId: Date.now(),
          itemCode: '',
          itemName: '',
          quantity: 1,
          unitPrice: 0,
          amount: 0
        }
      ]
    });
  };

  const handleRemoveItem = (itemId: number) => {
    setFormData({
      ...formData,
      items: formData.items.filter(item => item.itemId !== itemId)
    });
  };

  const handleItemChange = (itemId: number, field: keyof OrderItem, value: any) => {
    setFormData({
      ...formData,
      items: formData.items.map(item => {
        if (item.itemId === itemId) {
          const updatedItem = { ...item, [field]: value };
          if (field === 'quantity' || field === 'unitPrice') {
            updatedItem.amount = updatedItem.quantity * updatedItem.unitPrice;
          }
          return updatedItem;
        }
        return item;
      })
    });
  };

  useEffect(() => {
    const total = formData.items.reduce((sum, item) => sum + item.amount, 0);
    setFormData(prev => ({ ...prev, totalAmount: total }));
  }, [formData.items]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{viewMode ? 'View Order' : order ? 'Edit Order' : 'Create New Order'}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row form-row-3">
              <div className="form-group">
                <label>Order No *</label>
                <input
                  type="text"
                  required
                  disabled={viewMode}
                  value={formData.orderNo}
                  onChange={(e) => setFormData({ ...formData, orderNo: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Order Date *</label>
                <input
                  type="date"
                  lang="en-US"
                  required
                  disabled={viewMode}
                  value={formData.orderDate}
                  onChange={(e) => setFormData({ ...formData, orderDate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Delivery Date *</label>
                <input
                  type="date"
                  lang="en-US"
                  required
                  disabled={viewMode}
                  value={formData.deliveryDate}
                  onChange={(e) => setFormData({ ...formData, deliveryDate: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row form-row-3">
              <div className="form-group">
                <label>Customer Name *</label>
                <select
                  required
                  disabled={viewMode}
                  value={formData.customerCode}
                  onChange={(e) => handleCustomerChange(e.target.value)}
                >
                  <option value="">Select Customer</option>
                  {customers.filter(c => c.isActive === 'Y').map(customer => (
                    <option key={customer.customerId} value={customer.customerCode}>
                      {customer.customerName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Customer Code *</label>
                <input
                  type="text"
                  required
                  disabled
                  value={formData.customerCode}
                  readOnly
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  disabled={viewMode}
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                >
                  <option value="PENDING">Pending</option>
                  <option value="CONFIRMED">Confirmed</option>
                  <option value="IN_PRODUCTION">In Production</option>
                  <option value="READY">Ready for Delivery</option>
                  <option value="DELIVERED">Delivered</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Shipping Address *</label>
              <textarea
                required
                disabled={viewMode}
                value={formData.shippingAddress}
                onChange={(e) => setFormData({ ...formData, shippingAddress: e.target.value })}
                rows={2}
              />
            </div>

            <div className="form-section">
              <div className="section-header">
                <h4>Order Items</h4>
                {!viewMode && (
                  <button type="button" className="btn btn-sm btn-primary" onClick={handleAddItem}>
                    <MdAdd /> Add Item
                  </button>
                )}
              </div>

              <table className="item-table">
                <thead>
                  <tr>
                    <th>Item Code</th>
                    <th>Item Name</th>
                    <th>Quantity</th>
                    <th>Unit Price</th>
                    <th>Amount</th>
                    {!viewMode && <th>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map(item => (
                    <tr key={item.itemId}>
                      <td>
                        <input
                          type="text"
                          disabled={viewMode}
                          value={item.itemCode}
                          onChange={(e) => handleItemChange(item.itemId, 'itemCode', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          disabled={viewMode}
                          value={item.itemName}
                          onChange={(e) => handleItemChange(item.itemId, 'itemName', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          disabled={viewMode}
                          value={item.quantity}
                          onChange={(e) => handleItemChange(item.itemId, 'quantity', parseFloat(e.target.value))}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          disabled={viewMode}
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(item.itemId, 'unitPrice', parseFloat(e.target.value))}
                        />
                      </td>
                      <td>₱{item.amount.toLocaleString()}</td>
                      {!viewMode && (
                        <td>
                          <button
                            type="button"
                            className="btn-icon btn-delete"
                            onClick={() => handleRemoveItem(item.itemId)}
                          >
                            <MdDelete />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'right', fontWeight: 'bold' }}>Total:</td>
                    <td style={{ fontWeight: 'bold' }}>₱{formData.totalAmount.toLocaleString()}</td>
                    {!viewMode && <td></td>}
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="form-group">
              <label>Remarks</label>
              <textarea
                disabled={viewMode}
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {viewMode ? 'Close' : 'Cancel'}
            </button>
            {!viewMode && (
              <button type="submit" className="btn btn-primary">
                {order ? 'Update' : 'Create'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default SalesOrders;
